import { getI18n } from '@/plugins/i18n'

import { useAccountingStore } from '@/stores/accounting.store'

const DEFAULT_CURRENCY_DECIMALS = 2

/**
 * Decimals used everywhere a monetary value is rendered (grids, reports, editors).
 * Configured per tenant in CompanyData, so it is read here and nowhere else.
 * Display only: never round a value with it before saving — the scale accepted by
 * the API is the one of the model field, quantized backend-side on receipt.
 */
export const getCurrencyPrecision = () => {
  const accountingStore = useAccountingStore()
  return accountingStore.companyData?.currency_decimals ?? DEFAULT_CURRENCY_DECIMALS
}

export const getAmountDecimals = () => {
  const accountingStore = useAccountingStore()
  return accountingStore.companyData?.amount_decimals ?? DEFAULT_CURRENCY_DECIMALS
}

const getCurrentLocale = () => getI18n().locale.value

const currencyFormatterCache = new Map()

/**
 * Editable representation of a monetary value: the configured decimals (trailing zeros
 * included) with the separator of the user's locale. No grouping, so what is displayed
 * is what `parseCurrencyValue` reads back.
 */
export const formatCurrencyValue = (value, decimals = null, locale = getCurrentLocale()) => {
  if (value === null || value === undefined || value === '' || Number.isNaN(Number(value))) {
    return ''
  }
  const fractionDigits = decimals ?? getCurrencyPrecision()
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    useGrouping: false,
  }).format(Number(value))
}

const decimalFormatterCache = new Map()

/** A quantity in the user's locale with up to `maximumFractionDigits` decimals, no trailing zeros (the grids' `#0.##`). */
export const formatDecimal = (value, maximumFractionDigits = 2, locale = getCurrentLocale()) => {
  const key = `${locale}|${maximumFractionDigits}`
  if (!decimalFormatterCache.has(key)) {
    decimalFormatterCache.set(key, new Intl.NumberFormat(locale, { maximumFractionDigits }))
  }
  return decimalFormatterCache.get(key).format(Number(value) || 0)
}

/** Decimal separator of a locale, straight from Intl. */
const getDecimalSeparator = (locale) =>
  new Intl.NumberFormat(locale).formatToParts(1.1).find(part => part.type === 'decimal').value

/**
 * Read back a typed amount: everything that is not a digit, a sign or the decimal
 * separator is dropped (thousands separators included). A numeric keypad types `.` even
 * where the locale separator is `,`, so a lone `.` is taken as the decimal separator.
 */
export const parseCurrencyValue = (text, locale = getCurrentLocale()) => {
  if (text === null || text === undefined || text === '') {
    return null
  }
  const localeSeparator = getDecimalSeparator(locale)
  const separator = !String(text).includes(localeSeparator) && String(text).includes('.') ? '.' : localeSeparator
  const normalized = String(text)
    .replace(separator === ',' ? /[^0-9,-]/g : /[^0-9.-]/g, '')
    .replace(separator, '.')

  const value = Number(normalized)
  return normalized === '' || Number.isNaN(value) ? null : value
}

export const resolveCurrencyRef = (currencyRef) => currencyRef ?? useAccountingStore().companyData?.default_currency_ref

export const useCurrency = () => {
  const getCurrencyString = (value, currencyCode, locale = getCurrentLocale()) => {
    const formatter = getCurrencyFormatter(currencyCode, locale)
    return formatter.format(value)
  }

  const getCurrencyFormatter = (currencyCode, locale = getCurrentLocale()) => {
    const decimals = getCurrencyPrecision()
    const key = `${locale}|${currencyCode}|${decimals}`
    if (!currencyFormatterCache.has(key)) {
      currencyFormatterCache.set(key, new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
        useGrouping: true,
      }))
    }
    return currencyFormatterCache.get(key)
  }

  return {
    getCurrencyString,
    getCurrencyFormatter,
  }
}
